import { z } from "zod";
import { Challenge, SecretTask } from "./challenges.js";
/**
 * CONTENT PACKS — missions, prompts, Steward lines, clips and story arcs as data.
 *
 * The engine never contains a mission; it runs mission TEMPLATES from packs.
 * Growing from 5 missions to 40, or shipping a Christmas pack, is a content job.
 *
 * `schemaVersion` is bumped only for breaking changes; the loader can migrate
 * older packs forward.
 */
export const CONTENT_SCHEMA_VERSION = 1;
const id = z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "ids are kebab-case");
export const MISSION_FAMILIES = [
    "build", "relay", "communication", "memory", "trust", "performance", "hidden-info", "dexterity",
];
export const MISSION_SPACES = ["sofa", "room", "house", "garden"];
export const SABOTAGE_TYPES = ["none", "fake-instruction", "hidden-penalty", "time-steal", "false-digit"];
export const MissionTemplate = z.object({
    id,
    title: z.string().min(1),
    family: z.enum(MISSION_FAMILIES),
    summary: z.string().min(1),
    /** Ordered beats the phones and TV walk through. */
    steps: z.array(z.string().min(1)).min(1),
    durationSec: z.number().int().min(30).max(900),
    scavengeSec: z.number().int().min(0).max(180).default(0),
    /** Things players fetch from around the home. */
    objects: z.array(z.string().min(1)).default([]),
    minPlayers: z.number().int().min(2).max(16),
    teamSize: z.number().int().min(1).max(16).nullable().default(null),
    space: z.enum(MISSION_SPACES),
    noise: z.enum(["quiet", "lively", "loud"]),
    remoteSafe: z.boolean(),
    /** Spec safety rule: every mission must have a seated alternative. */
    seatedAlternative: z.string().min(1),
    sabotage: z.array(z.enum(SABOTAGE_TYPES)).min(1),
    potValue: z.number().int().min(0),
    tags: z.array(z.string()).default([]),
});
export const DiaryPrompt = z.object({
    id,
    text: z.string().min(1).max(140),
    audience: z.enum(["all", "faithful", "traitor"]).default("all"),
    moment: z.enum(["after-mission", "before-round-table", "any"]).default("any"),
    /** Sealed until the finale: never played at the Round Table, shown in "Uncloaked". */
    sealed: z.boolean().default(false),
});
export const StewardLine = z.object({
    id,
    /** Which cue the line can accompany, e.g. "lobby.arrival". */
    cue: z.string().min(1),
    /** {name} and other {placeholders} are filled at runtime. */
    text: z.string().min(1).max(200),
    tone: z.enum(["dry", "ominous", "playful", "grand"]).default("dry"),
    /** Which case of the cue it fits, e.g. "traitor" for a banishment, or a phase or mission kind. */
    when: z.string().optional(),
    /** False for lines too dark for family mode. */
    family: z.boolean().default(true),
    /** Optional pre-recorded audio; text-to-speech is used when absent. */
    audio: z.string().optional(),
});
export const CLIP_CATEGORIES = [
    "atmosphere", "steward", "mission-intro", "murder", "round-table", "reveal",
];
export const ClipMeta = z.object({
    id,
    category: z.enum(CLIP_CATEGORIES),
    src: z.string().min(1),
    durationMs: z.number().int().min(500).max(60_000),
    mood: z.enum(["calm", "suspicion", "tension", "reveal", "triumph"]),
    /** Clip has masked layers for name, sigil and cloak colour. */
    personalisable: z.boolean().default(false),
    tags: z.array(z.string()).default([]),
});
export const StoryArc = z.object({
    id,
    title: z.string().min(1),
    synopsis: z.string().min(1),
    locations: z.array(z.string().min(1)).min(3),
});
export const ContentPack = z
    .object({
    schemaVersion: z.literal(CONTENT_SCHEMA_VERSION),
    id,
    name: z.string().min(1),
    version: z.string().regex(/^\d+\.\d+\.\d+$/, "semver like 1.0.0"),
    familySafe: z.boolean().default(true),
    missions: z.array(MissionTemplate).default([]),
    diaryPrompts: z.array(DiaryPrompt).default([]),
    stewardLines: z.array(StewardLine).default([]),
    clips: z.array(ClipMeta).default([]),
    arcs: z.array(StoryArc).default([]),
    challenges: z.array(Challenge).default([]),
    secretTasks: z.array(SecretTask).default([]),
})
    .superRefine((pack, ctx) => {
    // Ids must be unique within each collection.
    for (const key of ["missions", "diaryPrompts", "stewardLines", "clips", "arcs", "challenges", "secretTasks"]) {
        const seen = new Set();
        pack[key].forEach((item, index) => {
            if (seen.has(item.id)) {
                ctx.addIssue({ code: "custom", path: [key, index, "id"], message: `Duplicate id "${item.id}"` });
            }
            seen.add(item.id);
        });
    }
});
export function parseContentPack(input) {
    return ContentPack.parse(input);
}
//# sourceMappingURL=content.js.map